import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { CurrencyProvider } from "@/lib/ecom/currency";
import { CartProvider } from "@/lib/ecom/cart";
import { CartDrawer } from "@/components/marketing/CartDrawer";
import { parsePath } from "@/lib/i18n/locale";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-background transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset?.();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-background transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "banana.cards" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@bananacards" },
      { name: "google-site-verification", content: "yQHTzdYcGCqo0gg1BztmOI2pswIueZDizNNcOPq-3BU" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { locale } = parsePath(pathname);
  return (
    <html lang={locale}>
      <head>
        <HeadContent />
        {/* First-visit auto-detect for Vietnam only. If the visitor looks Vietnamese
            (timezone Asia/Ho_Chi_Minh OR browser language starts with 'vi') AND they
            haven't picked a language yet, set lang=vi + country=VN and redirect to /vi.
            Runs inline before paint. Respects any existing user choice. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var c=document.cookie;if(c.indexOf('banana_lang=')>=0)return;var tz='';try{tz=Intl.DateTimeFormat().resolvedOptions().timeZone||'';}catch(e){}var lang=(navigator.language||'').toLowerCase();var isVN=tz==='Asia/Ho_Chi_Minh'||lang.indexOf('vi')===0;if(!isVN)return;var exp='; path=/; max-age=31536000; samesite=lax';document.cookie='banana_lang=vi'+exp;if(c.indexOf('banana_country=')<0){document.cookie='banana_country=VN'+exp;}var p=location.pathname;if(p.indexOf('/vi')!==0){location.replace((p==='/'?'/vi':'/vi'+p)+location.search+location.hash);}}catch(e){}})();`,
          }}
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <CurrencyProvider>
        <CartProvider>
          <Outlet />
          <CartDrawer />
        </CartProvider>
      </CurrencyProvider>
    </QueryClientProvider>
  );
}
